// src/pages/PastNumbers.js
import React, { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  collection,
  getDocs
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
// NOTE: no Header/Footer imports — this page is wrapped in <MainLayout> by
// App.js, which already renders them (importing them here doubled the header).

const GAMES = [
  { key: 'PB', label: 'Powerball', ballLabel: 'PB' },
  { key: 'MM', label: 'Mega Millions', ballLabel: 'MB' }
];

// Tickets are stored as preformatted strings like "12-23-34-45-56 PB-14".
// Split into white balls + special ball for light formatting; fall back to
// rendering the raw value if it doesn't match.
const renderTicket = (ticket) => {
  if (typeof ticket !== 'string') return String(ticket);
  const match = ticket.match(/^(.+?)\s+((?:PB|MB)-\d+)$/);
  if (!match) return ticket;
  return (
    <>
      {match[1]}{' '}
      <span style={{ fontWeight: 'bold', color: '#A6CE3A' }}>{match[2]}</span>
    </>
  );
};

const PastNumbers = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedKey, setSelectedKey] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      if (!user) return navigate('/login');
      setLoading(true);
      setLoadError(false);
      try {
        const db = getFirestore();

        // One doc per draw lives at member_assigned_numbers/{PB|MM}/{email}/{drawDate}.
        // Plain collection fetch (no orderBy) — ordering by documentId DESC
        // requires a Firestore index ("failed-precondition" in production);
        // a member only has one doc per draw, so we sort/cap client-side.
        const snaps = await Promise.all(
          GAMES.map(game =>
            getDocs(collection(db, 'member_assigned_numbers', game.key, user.email))
          )
        );

        const merged = [];
        snaps.forEach((snap, i) => {
          const game = GAMES[i];
          const gameEntries = [];
          snap.forEach(docSnap => {
            const data = docSnap.data() || {};
            // drawDate is normally a "YYYY-MM-DD" string, but normalize
            // defensively (legacy docs could hold a Firestore Timestamp).
            const raw = data.drawDate;
            const drawDate = typeof raw === 'string'
              ? raw
              : (raw && typeof raw.toDate === 'function'
                ? raw.toDate().toISOString().slice(0, 10)
                : docSnap.id);
            gameEntries.push({
              drawDate,
              gameLabel: game.label,
              numberSets: Array.isArray(data.numberSets) ? data.numberSets : []
            });
          });
          // Newest first; keep the full history so the dropdown can reach
          // any past draw (one small doc per draw — volume stays tiny).
          gameEntries.sort((a, b) => b.drawDate.localeCompare(a.drawDate));
          merged.push(...gameEntries);
        });

        // Draw dates are ISO (YYYY-MM-DD), so string compare sorts correctly.
        merged.sort((a, b) => b.drawDate.localeCompare(a.drawDate));
        setEntries(merged);
      } catch (err) {
        // Permission/network failure must not silently render "No numbers yet."
        console.error('❌ Failed to load past numbers:', err);
        setErrorCode(err && (err.code || err.message) ? String(err.code || err.message) : 'unknown');
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [navigate, reloadKey]);

  return (
    <>
      {/* green sub‑bar */}
      <div
        style={{
          backgroundColor: '#A6CE3A',
          height: '75px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <h2 style={{
          margin: 0,
          color: '#FFF',
          fontFamily: "'Open Sans', sans-serif",
          fontWeight: 'bold',
          fontSize: '2rem'
        }}>
          All Past Assigned Numbers
        </h2>
      </div>

      <div style={{ padding:'2rem', maxWidth:'700px', margin:'0 auto', fontFamily:"'Open Sans', sans-serif" }}>
        {loadError ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              backgroundColor: '#fef2f2',
              border: '2px solid #ef4444',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1rem',
              color: '#dc2626'
            }}>
              <h3 style={{ margin: '0 0 0.5rem 0' }}>⚠️ Couldn't load your numbers</h3>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                Something went wrong loading your past numbers. Please check your connection and try again.
              </p>
              {errorCode && (
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
                  ({errorCode})
                </p>
              )}
            </div>
            <button
              onClick={() => setReloadKey(k => k + 1)}
              style={{
                padding: '0.875rem 1.5rem',
                backgroundColor: '#A6CE3A',
                color: 'white',
                fontWeight: '600',
                fontSize: '1rem',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                minHeight: '48px'
              }}
            >
              🔄 Try Again
            </button>
          </div>
        ) : loading ? (
          <p style={{ textAlign: 'center' }}>Loading…</p>
        ) : entries.length === 0 ? (
          <p>No numbers yet.</p>
        ) : (() => {
          // Most recent draw for each game, shown expanded by default.
          const latest = GAMES
            .map(g => entries.find(e => e.gameLabel === g.label))
            .filter(Boolean)
            .sort((a, b) => b.drawDate.localeCompare(a.drawDate));
          const latestKeys = new Set(latest.map(e => `${e.gameLabel}|${e.drawDate}`));
          // Everything older lives in the dropdown.
          const older = entries.filter(e => !latestKeys.has(`${e.gameLabel}|${e.drawDate}`));
          const selected = older.find(e => `${e.gameLabel}|${e.drawDate}` === selectedKey) || null;

          const drawBlock = (e) => (
            <div key={`${e.gameLabel}-${e.drawDate}`} style={{ marginBottom: '2rem' }}>
              <h4 style={{ marginBottom: '0.5rem' }}>
                {e.drawDate}
                <span style={{ fontWeight: 'normal', color: '#666' }}> — {e.gameLabel}</span>
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {e.numberSets.map((ticket, j) => (
                  <li key={j} style={{ padding: '0.15rem 0' }}>{renderTicket(ticket)}</li>
                ))}
              </ul>
            </div>
          );

          return (
            <>
              {latest.map(drawBlock)}

              {older.length > 0 && (
                <div style={{
                  borderTop: '1px solid #e5e7eb',
                  paddingTop: '1.5rem',
                  marginTop: '0.5rem'
                }}>
                  <h4 style={{ margin: '0 0 0.5rem 0' }}>Earlier draws</h4>
                  <select
                    value={selectedKey}
                    onChange={e => setSelectedKey(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      fontFamily: "'Open Sans', sans-serif",
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      backgroundColor: '#fff',
                      marginBottom: '1.5rem',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select an earlier draw…</option>
                    {older.map(e => (
                      <option
                        key={`${e.gameLabel}|${e.drawDate}`}
                        value={`${e.gameLabel}|${e.drawDate}`}
                      >
                        {e.drawDate} — {e.gameLabel}
                      </option>
                    ))}
                  </select>

                  {selected && drawBlock(selected)}
                </div>
              )}
            </>
          );
        })()}
      </div>

    </>
  );
};

export default PastNumbers;
